import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Loader2, PenLine, ShieldCheck } from "lucide-react";
import { humanAdoptionStatus } from "@/lib/humanAdoptionStatus";
import AdoptionSignerProgressList from "./AdoptionSignerProgressList";
import type { AdoptionRow, AdoptionSigner } from "@/hooks/useCurrentEditionAdoption";
import { useNavigate } from "react-router-dom";

interface Props {
  eligible: boolean;
  adoption: AdoptionRow | null;
  signers: AdoptionSigner[];
  signedCount: number;
  onChanged: () => void;
}

/**
 * The "do" part of the page. One card, one state at a time.
 * The member never sees a document before a person on our team has read it.
 */
export default function AdoptionFlowPanel({
  eligible,
  adoption,
  signers,
  signedCount,
  onChanged,
}: Props) {
  const [starting, setStarting] = useState(false);
  const [opening, setOpening] = useState(false);
  const navigate = useNavigate();

  const start = async () => {
    setStarting(true);
    const { data, error } = await supabase.functions.invoke("adoption-initiate", { body: {} });
    setStarting(false);

    if (error || (data as any)?.error) {
      let message = "That did not go through. Please try again.";
      try {
        const parsed = await (error as any)?.context?.json?.();
        if (parsed?.error) message = parsed.error;
      } catch (_e) {
        if ((data as any)?.error) message = (data as any).error;
      }
      toast.error(message);
      return;
    }

    toast.success("We are preparing your set now.");
    onChanged();
  };

  /** Members always open the copy through a short lived private link. */
  const openCopy = async () => {
    if (!adoption?.delivered_pdf_path) return;
    setOpening(true);
    const { data, error } = await supabase.storage
      .from("documents")
      .createSignedUrl(adoption.delivered_pdf_path, 3600);
    setOpening(false);

    if (error || !data?.signedUrl) {
      toast.error("We could not open that copy right now. Please try again.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  if (!eligible && !adoption) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">What to do</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            This step is not open for your Association yet. We will let you know here as soon as it
            is.
          </p>
        </CardContent>
      </Card>
    );
  }

  const status = adoption?.status ?? "eligible";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">What to do</CardTitle>
          <Badge variant="secondary">{humanAdoptionStatus(status)}</Badge>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {(!adoption || status === "eligible") && (
            <>
              <p>
                When you are ready, start here. We will prepare your set: the meeting notice, the
                minutes, the resolution that adopts the current edition, the Constitution itself,
                and a page for every member to sign.
              </p>
              <Button onClick={start} disabled={starting} className="min-h-11 w-full sm:w-auto">
                {starting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting
                  </>
                ) : (
                  "Begin the adoption"
                )}
              </Button>
            </>
          )}

          {status === "generating" && (
            <div className="flex items-start gap-3">
              <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="font-medium">We are preparing your set.</p>
                <p className="text-muted-foreground">
                  This page keeps itself up to date. You can leave and come back.
                </p>
              </div>
            </div>
          )}

          {status === "in_review" && (
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">A person on our team is reading it now.</p>
                <p className="text-muted-foreground">
                  Nothing reaches you until a person has checked every page. We will show it here as
                  soon as it passes.
                </p>
              </div>
            </div>
          )}

          {(status === "ready_to_sign" || status === "awaiting_signatures") && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <PenLine className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Your set is ready to sign.</p>
                  <p className="text-muted-foreground">
                    Every member gets an email with a link to sign. You can sign in the portal or
                    print it and sign it by hand. Either way is fine.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={openCopy}
                disabled={opening || !adoption?.delivered_pdf_path}
                className="min-h-11 w-full sm:w-auto"
              >
                {opening ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="mr-2 h-4 w-4" />
                )}
                Read your copy
              </Button>
            </div>
          )}

          {status === "paused" && (
            <div>
              <p className="font-medium">This is on hold.</p>
              <p className="text-muted-foreground">
                {adoption?.paused_reason ?? "Our team is looking at it and will be in touch."}
              </p>
            </div>
          )}

          {status === "complete" && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Adopted. Your set is filed.</p>
                  <p className="text-muted-foreground">
                    Effective as of {adoption?.adoption_effective_date ?? "the adoption date"}, signed
                    on {adoption?.executed_on ?? "the signing date"}. The edition you held before is
                    kept and marked as replaced.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={openCopy}
                  disabled={opening}
                  className="min-h-11"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Read your copy
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/governance/record")}
                  className="min-h-11"
                >
                  Open the Record
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {(status === "ready_to_sign" ||
        status === "awaiting_signatures" ||
        status === "paused" ||
        status === "complete") && (
        <AdoptionSignerProgressList signers={signers} signedCount={signedCount} />
      )}
    </div>
  );
}
